
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ToastService } from 'src/app/core/services/toast.service';
import { Router } from '@angular/router';
import { ApiValidationError } from 'src/app/shared/models/api-validation.model';
import { normalizeApiValidationError } from 'src/app/shared/utils/api-validation.util';
import {
  CreateEstampOrderRequest,
  EstampApiService
} from 'src/app/core/services/estamp-api.service';
import { EstampApiKeyService } from 'src/app/core/services/estamp-api-key.service';

@Component({
  selector: 'app-create-customer-order',
  templateUrl: './create-customer-order.component.html',
  styleUrl: './create-customer-order.component.scss'
})
export class CreateCustomerOrderComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInput: any;

  // Send Stamp Document Tab
  selectedFile!: File;
  loading: boolean = false;
  apiValidationError: ApiValidationError | null = null;
  orderForm!: FormGroup;
  firstPartyName: any = {};
  states: any[] = [];
  fileSizeError: string | null = null;
  readonly maxFileSizeBytes: number = 10 * 1024 * 1024; // 10MB



  private subscriptions = new Subscription();

  constructor
    (
      private fb: FormBuilder,
      private estampApi: EstampApiService,
      private apiKeyService: EstampApiKeyService,
      private router: Router,
      private toastService: ToastService,
    ) { }

  ngOnInit() {
    this.createOrder();

    this.firstPartyName = 'Demo';

    this.subscriptions.add(
      this.apiKeyService.changes.subscribe(() => {
        this.states = [];

        if (this.apiKeyService.hasApiKey) {
          this.getStates();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  navigate = () => {
    this.router.navigate(['/customer/estamp/customer-order-list']);
  }
  navigateInventory() {
    this.router.navigate(['/customer/estamp/inventory']);
  }

  // ===== SEND STAMP DOCUMENT TAB =====
  createOrder() {
    this.orderForm = this.fb.group({
      stateId: ['', Validators.required],
      denomination: ['', Validators.required],
      purpose: ['', Validators.required],
      secondParty: ['', Validators.required],
      appendStampPosition: ['PRE', Validators.required]
    });

  }

  /** Collection request 1: GET eSTAMP STATES LIST */
  getStates() {
    this.estampApi.getStates().subscribe(
      (res: any) => {
        this.states = this.extractStates(res);
      },
      () => { }
    );
  }

  get hasApiKey(): boolean {
    return this.apiKeyService.hasApiKey;
  }

  /** Collection request 2: CREATE eSTAMP ORDER */
  onSubmit() {
    if (this.loading) {
      return;
    }

    // Purana API error submit dobara karne par clear hoga
    this.apiValidationError = null;

    if (!this.apiKeyService.hasApiKey) {
      this.apiValidationError = {
        message: '',
        errors: [
          'API Key is required. Add your x-api-key at the top of this page.'
        ]
      };

      return;
    }

    if (!this.orderForm.valid) {
      this.toastService.showToaster(
        "Warning",
        "Please provide all required data.",
        "info"
      );

      this.loading = false;
      return;
    }

    if (!this.selectedFile) {
      this.toastService.showToaster(
        "Warning",
        "Please select file.",
        "info"
      );

      this.loading = false;
      return;
    }

    const formValue = this.orderForm.value;
    const request: CreateEstampOrderRequest = {
      stateId: formValue.stateId,
      denomination: formValue.denomination,
      purpose: formValue.purpose,
      appendStampPosition: formValue.appendStampPosition,
      secondParty: formValue.secondParty,
      firstParty: 'Demo',
      file: this.selectedFile
    };

    this.loading = true;

    this.estampApi.createOrder(request, true).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.apiValidationError = null;

        this.router.navigate([
          '/customer/estamp/customer-order-list'
        ]);

        this.toastService.showToaster(
          "Success",
          res?.message || "Order created successfully.",
          "success"
        );
      },

      error: (error: any) => {
        this.loading = false;

        // Create-order API error ab red box me jayega
        this.apiValidationError =
          normalizeApiValidationError(error);
      }
    });
  }

  allowInput(event: KeyboardEvent, type: 'number' | 'text') {
    const char = String.fromCharCode(event.keyCode);

    if (type === 'number') {
      if (!/^[0-9]$/.test(char)) {
        event.preventDefault();
      }
    }

    if (type === 'text') {
      if (!/^[a-zA-Z ]$/.test(char)) {
        event.preventDefault();
      }
    }
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    this.fileSizeError = null;
    if (!file) {
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      this.selectedFile = undefined as any;
      event.target.value = '';
      this.toastService.showToaster(
        "Invalid File",
        "Please upload the required document in PDF format only.",
        "info"
      );
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.selectedFile = undefined as any;
      event.target.value = '';
      this.fileSizeError = `File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds the maximum limit of 10MB. Please upload a smaller file.`;
      this.toastService.showToaster(
        "Invalid File",
        this.fileSizeError,
        "info"
      );
      return;
    }

    this.selectedFile = file;
  }

  clearApiValidationMessage(): void {
    this.apiValidationError = null;
  }

  private extractStates(res: any): any[] {
    const states =
      res?.data?.states ??
      res?.data ??
      res?.states ??
      res;

    return Array.isArray(states) ? states : [];
  }

}
